/* Materia Themes. Developed By Materia Themes. */
// Scroll animations — bulletproofed

const SCROLL_ANIMATION_TRIGGER_CLASSNAME = '.scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ANIMATION_ARMED_CLASSNAME = 'scroll-trigger--armed';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const SCROLL_ANIMATION_DONE_ATTR = 'data-animation-done';

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    return fn(...args);
  };
}

function reducedMotion() {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isInViewport(el) {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight || document.documentElement.clientHeight;
  return rect.bottom > 0 && rect.top < vh;
}

function markDone(el) {
  if (el.hasAttribute(SCROLL_ANIMATION_DONE_ATTR)) return;
  el.setAttribute(SCROLL_ANIMATION_DONE_ATTR, '');
  el.setAttribute('animation-end', '');
  el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
}

function attachEndHandler(el) {
  if (el._blsAnimHooked) return;
  el._blsAnimHooked = true;
  const handler = () => markDone(el);
  el.addEventListener('animationend', handler, { once: false });
  // Belt-and-suspenders: also listen on descendants for animations that animate children
  el.addEventListener(
    'animationend',
    (e) => {
      if (e.target !== el) markDone(el);
    },
    true
  );
}

function onIntersection(entries, observer) {
  entries.forEach((entry) => {
    const el = entry.target;
    if (entry.isIntersecting) {
      el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      if (el.hasAttribute('data-cascade')) {
        const parent = el.parentElement;
        const sibs = parent ? Array.from(parent.children).filter((c) => c.classList.contains('scroll-trigger')) : [el];
        const idx = sibs.indexOf(el);
        el.style.setProperty('--animation-order', idx >= 0 ? idx : 0);
      }
      observer.unobserve(el);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const all = Array.from(rootEl.querySelectorAll(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (all.length === 0) return;

  if (isDesignModeEvent) {
    all.forEach((el) => {
      el.classList.add('scroll-trigger--design-mode');
      el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      markDone(el);
    });
    return;
  }

  if (reducedMotion()) {
    all.forEach((el) => {
      el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      markDone(el);
    });
    return;
  }

  // Pre-compute initial visibility to avoid the white-blank flash for below-fold elements.
  // Adding `--armed` here is what enables the CSS animation rules — without JS
  // (e.g., Shopify section-picker preview thumbnails) the class is never added,
  // so content stays at its default visible state and previews don't blank out.
  all.forEach((el) => {
    attachEndHandler(el);
    el.classList.add(SCROLL_ANIMATION_ARMED_CLASSNAME);
    if (isInViewport(el)) {
      el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    } else {
      el.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    }
  });

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
    threshold: 0,
  });
  all.forEach((el) => observer.observe(el));

  // Safety net 1: 2s after init, reveal anything already in view that's still marked offscreen.
  setTimeout(() => {
    all.forEach((el) => {
      if (el.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME) && isInViewport(el)) {
        el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      }
    });
  }, 2000);

  // Safety net 2: anything still invisible 6s after init is force-revealed (prevents the blank-section bug).
  setTimeout(() => {
    all.forEach((el) => {
      if (!el.hasAttribute(SCROLL_ANIMATION_DONE_ATTR)) markDone(el);
    });
  }, 6000);
}

// Zoom in on scroll
function initializeScrollZoomAnimationTrigger() {
  if (reducedMotion()) return;

  const els = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (els.length === 0) return;

  const scaleAmount = 0.2 / 100;

  els.forEach((el) => {
    let visible = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visible = entry.isIntersecting;
      });
    });
    observer.observe(el);

    el.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(el));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!visible) return;
        el.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(el));
      }, 50),
      { passive: true }
    );
  });
}

function percentageSeen(el) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const y = el.getBoundingClientRect().top + scrollY;
  const h = el.offsetHeight;

  if (y > scrollY + viewportHeight) return 0;
  if (y + h < scrollY) return 100;
  const distance = scrollY + viewportHeight - y;
  return Math.round(distance / ((viewportHeight + h) / 100));
}

// Bootstrap
function bootAnimations() {
  document.querySelectorAll('.swiper-slide.opacity-0').forEach((el) => el.classList.remove('opacity-0'));
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootAnimations);
} else {
  bootAnimations();
}

// Final reveal pass when everything is loaded — guarantees no elements are left stuck.
window.addEventListener('load', () => {
  setTimeout(() => {
    document.querySelectorAll('.' + SCROLL_ANIMATION_OFFSCREEN_CLASSNAME).forEach((el) => {
      if (isInViewport(el)) el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
  }, 600);
});

// Design mode — reset animations on section load/reorder
if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
  });
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
  });
}
